import { Message } from 'view-design'
import { computed, reactive, toRefs } from 'vue'
import { debounce, getImageSizeByObjectUrl } from '@/common/js/utils.js'
import { IMAGE_TYPES, UPLOAD_TYPE, GROUP_IMAGE_TYPE, GROUP_IMAGE_TYPE_LIST } from '../consts.js'

const isImageFileType = (type) => {
  return ['image/png', 'image/gif', 'image/jpeg'].includes(type)
}

const isVideoFileType = (type) => {
  return ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/webm', 'video/ogg', 'application/octet-stream'].includes(type)
}

const allowedSizes = ['1920*1080', '1080*1920', '1280*720', '720*1280']

function getMediaSize(file) {
  return new Promise(resolve => {
    if (file.type.startsWith('image')) {
      getImageSizeByObjectUrl(file).then(res => resolve({ ...res, type: 'image' }))
    }

    if (file.type.startsWith('video')) {
      const video = document.createElement('video')
      video.src = URL.createObjectURL(file)
      video.onloadedmetadata = () => resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        type: 'video'
      })
    }
  })
}
async function sizeValid(file) {
  const fileInfo = await getMediaSize(file)
  if (!allowedSizes.includes(`${fileInfo.width}*${fileInfo.height}`)) {
    const { width, height } = fileInfo
    Message.error({
      duration: 10,
      content: `失败：素材名称：${file.name}，尺寸：${width}px * ${height}px，尺寸不符合要求（1280*720，720*1280，1920*1080，1080*1920）`
    })
    return Promise.reject(new Error())
  }
  return Promise.resolve()
}

export const useBeforeUpload = (uploadList) => {
  async function videoValidator(file) {
    const { size, name, type } = file
    // 验证
    if (!isVideoFileType(type)) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的视频文件 ${name} 格式错误，只支持 mp4、wmv、webm、flv 格式`
      })
      return Promise.reject(new Error())
    }
    if (size > 1024 * 1024 * 300) {
      Message.error({ closable: true, duration: 5, content: `上传的视频文件 ${name} 超过限制大小300M` })
      return Promise.reject(new Error())
    }

    return true
  }

  async function imageValidator(file) {
    const { size, name, type } = file
    if (!isImageFileType(type)) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的图片 ${name} 格式错误，只支持 jpg、jpeg、png、gif 格式 `
      })
      return Promise.reject(new Error())
    }
    if (size > 1024 * 1024 * 10) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的图片${name}超过限制大小10M`
      })
      return Promise.reject(new Error())
    }

    return Promise.resolve()
  }

  // 视频素材上传拦截器
  const videoUploadInterceptor = (file) => {
    if (uploadList.value.length > 30) {
      Message.error('批量上传单次最多30个素材')
      return Promise.reject(new Error())
    }
    return videoValidator(file) && sizeValid(file)
  }

  // 图片素材上传拦截器
  const imageUploadInterceptor = (file) => {
    if (uploadList.value.length > 30) {
      Message.error('批量上传单次最多30个素材')
      return Promise.reject(new Error())
    }
    return imageValidator(file)
  }

  // 试玩素材上传拦截器
  const playableImageUploadInterceptor = (file) => {
    if (uploadList.value.length > 1) {
      Message.error('批量上传单次最多1个素材')
      return Promise.reject(new Error())
    }
    return imageValidator(file)
  }

  // 音频素材上传拦截器
  const audioUploadInterceptor = (file) => {
    if (uploadList.value.length > 10) {
      Message.error('批量上传单次最多10个素材')
      return Promise.reject(new Error())
    }
    const { size, name, type } = file
    if (!['audio/mpeg', 'audio/x-m4a'].includes(type)) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的音频 ${name} 格式错误，只支持 mp3、m4a 格式`
      })
      return Promise.reject(new Error())
    }
    if (size > 1024 * 1024 * 100) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的音频 ${name} 超过限制大小100M`
      })
      return Promise.reject(new Error())
    }
    return Promise.resolve()
  }

  // 元素材上传拦截器
  const metaMaterialUploadInterceptor = (file) => {
    if (uploadList.value.length > 30) {
      Message.error('批量上传单次最多30个素材')
      return false
    }

    const isImage = isImageFileType(file.type)
    const isVideo = isVideoFileType(file.type)
    if (!isImage && !isVideo) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的素材 ${file.name}格式错误，只支持 jpg、jpeg、png、gif、mp4、wmv、webm、flv 格式`
      })

      return false
    }
    if (isImage) {
      return imageValidator(file)
    }
    if (isVideo) {
      return videoValidator(file)
    }

    return true
  }

  return {
    audioUploadInterceptor,
    imageUploadInterceptor,
    playableImageUploadInterceptor,
    videoUploadInterceptor,
    metaMaterialUploadInterceptor
  }
}

export const useComputeds = (formData) => {
  // 创意资源素材类型
  const lagecyMaterialEnabled = computed(() => [IMAGE_TYPES.IMAGE, IMAGE_TYPES.VIDEO, IMAGE_TYPES.GROUP_IMAGE, IMAGE_TYPES.LOGO].includes(formData.imageType))
  // 上传类型：新素材
  const materialType = computed(() => {
    return {
      // 新素材上传
      new: formData.uploadType === UPLOAD_TYPE.NEW,
      // 绑定老素材
      legacy: formData.uploadType === UPLOAD_TYPE.LEGACY
    }
  })

  return {
    lagecyMaterialEnabled,
    materialType
  }
}

export const useFormRules = (formData, uploadList, uploadedGroupImageListValidator) => {
  const { materialType } = useComputeds(formData)
  const uploadFormRules = () => {
    return {
      uploadList: [
        {
          required: true,
          validator(rule, value, callback) {
            if (!uploadList.value.length) {
              callback(new Error('上传素材不能为空'))
            }
            callback()
          }
        }
      ],
      bindMaterId: [
        {
          required: true,
          validator(rule, value, callback) {
            const { bindMaterId, uploadType } = formData
            if (uploadType === 2 && !bindMaterId) {
              callback(new Error('请选择需要绑定老素材'))
            }
            callback()
          }
        }
      ],
      opId: [
        { required: true, message: '设计者不能为空', trigger: 'blur' }
      ],
      imageStyles: [
        { required: true, message: '素材样式不能为空', trigger: 'blur' },
        { type: 'array', min: 1, message: '素材样式不能为空', trigger: 'change' },
        {
          validator(rule, value, callback) {
            const requiredIds = [28953, 28955]
            const selectedIds = value.flat()
            const isIncludeRequired = requiredIds.every(id => selectedIds.includes(id))
            isIncludeRequired ? callback() : callback(new Error('必选制作方式和素材来源不能为空'))
          },
          trigger: 'change'
        }
      ],
      game: [{
        // 元视频不需要校验游戏必填
        required: formData.imageType !== IMAGE_TYPES.META,
        validator(rule, value, callback) {
          if (materialType.value.new && [IMAGE_TYPES.IMAGE, IMAGE_TYPES.VIDEO, IMAGE_TYPES.GROUP_IMAGE, IMAGE_TYPES.LOGO, IMAGE_TYPES.PLAYABLE].includes(formData.imageType) && !value?.length) {
            callback(new Error('游戏不能为空'))
          }

          callback()
        }
      }],

      gdtPlayableId: [
        { required: formData.imageType === IMAGE_TYPES.PLAYABLE, message: '试玩ID不能为空', trigger: 'blur' },
        { min: 5, max: 150, message: '试玩ID长度在5到150个字符之间', trigger: 'blur' }

      ],

      playableMaterialName: [
        { required: formData.imageType === IMAGE_TYPES.PLAYABLE, message: '试玩素材名称不能为空', trigger: 'blur' },
        { min: 2, max: 50, message: '试玩素材名称长度在2到50个字符之间', trigger: 'blur' }
      ],

      overseas: [
        {
          validator(rule, value, callback) {
            if (materialType.value.new && value) {
              const { facebookImageId, googleImageId, tiktokImageId } = formData
              if (!facebookImageId && !googleImageId && !tiktokImageId) {
                callback(new Error('海外媒体素材至少填写1个'))
              }
            }

            callback()
          }
        }
      ],
      groupImageList: [
        {
          validator: uploadedGroupImageListValidator
        }
      ]
    }
  }

  return {
    uploadFormRules
  }
}

export const useVideoProcessFormRules = (processForm) => {
  const videoProcessFormRules = () => {
    const { watertype } = processForm
    return {
      paster: [
        {
          validator(rule, value, callback) {
            const { position, url } = value

            if (!url) {
              return callback(new Error('贴纸不能为空'))
            }

            if (!position) {
              return callback(new Error('位置不能为空'))
            }

            callback()
          }
        }
      ],
      waterwark: [
        {
          validator(rule, value, callback) {
            const { paster, word } = value
            if (watertype === 1) {
              if (!paster.url) return callback(new Error('贴纸不能为空'))
            }

            if (watertype === 2) {
              if (!word.text.trim()) return callback(new Error('水印文字不能为空'))
            }
            callback()
          }
        }
      ],
      warnnings: [
        {
          validator(rule, value, callback) {
            const { text, position } = value
            if (!position) {
              return callback(new Error('位置不能为空'))
            }
            if (!text) {
              return callback(new Error('警示语不能为空'))
            }
            callback()
          }
        }
      ],
      extendSize: [
        {
          validator(rule, value, callback) {
            if (!value?.length) {
              return callback(new Error('衍生尺寸不能为空'))
            }
            callback()
          }
        }
      ]
    }
  }

  return {
    videoProcessFormRules
  }
}

/**
 * @description: 组图上传
 * @return {*}
 */
export const useGroupImageUpload = () => {
  const groupImageModel = reactive({
    groupImageList: [],
    groupImageNames: [],
    groupImageUploadList: [],
    currentGroupImageType: '',
    groupImageUploaderRef: null,
    groupImageUploadLoading: false
  })

  const imageCount = computed(() => {
    return GROUP_IMAGE_TYPE_LIST.find((item) => item.value === groupImageModel.currentGroupImageType)?.imageCount
  })

  function uploadedGroupImageListValidator(rule, value, callback) {
    if (!groupImageModel.groupImageList?.length) {
      return callback(new Error('组图素材不能为空'))
    }

    for (let j = 0; j < groupImageModel.groupImageList.length; j++) {
      const group = groupImageModel.groupImageList[j]
      const groupIndex = j + 1
      const { width, height, imageCount } = group[0]
      const groupImageName = groupImageModel.groupImageNames[j]
      if (imageCount !== group.length) {
        return callback(new Error(`组图素材的数量须${imageCount}个`))
      }

      if (!groupImageName) {
        return callback(new Error(`组图素材${groupIndex}：名称不能为空`))
      }

      const firstItemShape = `${width}x${height}`
      const isSameShape = group.every((img) => {
        const { width, height } = img || {}
        const currentShape = `${width}x${height}`
        return currentShape === firstItemShape
      })

      if (!isSameShape) {
        return callback(new Error(`组图素材${groupIndex}：尺寸必须一致`))
      }

      for (let i = 0; i < group.length; i++) {
        const img = group[i]

        // 不能大于400kb
        if (img.size > 1024 * 400) {
          return callback(new Error(`组图素材${groupIndex}：图片${i + 1}（${img.name}）大小不能超过400kb`))
        }

        if (img.imageType === GROUP_IMAGE_TYPE.IMG_THREE_3_2) {
          if (img.width / img.height !== 3 / 2) {
            return callback(new Error(`组图素材${groupIndex}：宽高比必须为3:2`))
          }
        } else {
          if (img.width / img.height !== 1) {
            return callback(new Error(`组图素材${groupIndex}：宽高比必须为1:1`))
          }
        }
      }
    }
    callback()
  }

  const imageInterceptor = (file) => {
    const { size, name, type } = file
    if (!isImageFileType(type)) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的图片 ${name} 格式错误，只支持 jpg、jpeg、png、gif 格式 `
      })
      return false
    }
    if (size > 1024 * 1024 * 10) {
      Message.error({
        closable: true,
        duration: 5,
        content: `上传的图片${name}超过限制大小10M`
      })
      return false
    }
    return true
  }

  function groupImageUploadInterceptor() {
    const selectedCounts = groupImageModel.groupImageUploadList.length
    const imageCountValid = selectedCounts === imageCount.value
    const isSameSuffix = new Set(groupImageModel.groupImageUploadList.map(file => file.raw.name.split('.').pop()))?.size === 1
    if (!isSameSuffix) {
      const fileNames = groupImageModel.groupImageUploadList.map(file => file.raw.name).join(', ')
      const msg = `上传素材的格式必须一致，${fileNames}`
      Message.error({
        duration: 7,
        content: msg
      })
      return Promise.reject(new Error(msg))
    }

    // selectedCounts 为0不验证，groupImageModel.groupImageUploaderRef.clearFiles() 清空后selectedCounts为0
    if (!imageCountValid && selectedCounts > 0) {
      const msg = `上传素材数量必须为${imageCount.value}个`
      Message.error(msg)
      return Promise.reject(new Error(msg))
    }

    for (const file of groupImageModel.groupImageUploadList) {
      const isImageValid = imageInterceptor(file.raw)
      if (!isImageValid) return Promise.reject(new Error(''))
    }
    return Promise.resolve()
  }

  function onGroupImageUploadSuccess(res, file, fileList) {
    const isAllSuccess = fileList.every((file) => file.status === 'success')
    if (isAllSuccess) {
      const files = fileList.map(file => {
        const { width, height, url, name, signature, size } = file.response.data
        const suffix = name.split('.').pop()
        return {
          id: file.uid,
          size,
          width,
          height,
          preview_url: url,
          name: name,
          suffix,
          signature,
          imageCount: imageCount.value,
          imageType: groupImageModel.currentGroupImageType
        }
      })
      groupImageModel.groupImageList.push(files)
      groupImageModel.groupImageUploadLoading = false
      groupImageModel.groupImageUploaderRef.clearFiles()
    }
  }

  function onSelectedGroupImageRemove(index) {
    groupImageModel.groupImageList.splice(index, 1)
    groupImageModel.groupImageNames[index] = ''
  }

  function onGroupImageUpload(file, fileList) {
    debounce(() => {
      groupImageModel.groupImageUploadList = fileList
      if (fileList?.length) {
        groupImageUploadInterceptor().then(() => {
          groupImageModel.groupImageUploadLoading = true
          groupImageModel.groupImageUploaderRef.submit()
        }).catch((e) => {
          groupImageModel.groupImageUploaderRef.clearFiles()
          console.log('onGroupImageUpload error output: ', e)
        })
      }
    }, 800)()
  }

  function groupImageUploaderTrigger(cmd) {
    groupImageModel.currentGroupImageType = cmd
    const uploadEl = document.querySelector('.el-upload-dragger')
    uploadEl.click()
  }

  return {
    ...toRefs(groupImageModel),
    imageCount,
    onGroupImageUploadSuccess,
    groupImageUploadInterceptor,
    onGroupImageUpload,
    onSelectedGroupImageRemove,
    groupImageUploaderTrigger,
    uploadedGroupImageListValidator
  }
}
